import React from "react";
import { BsLinkedin } from "react-icons/bs";
import { motion } from "framer-motion";
import Header from "../components/Header";
import { Link, navigate } from "../router";
import { LINKEDIN_URL } from "../config";

const Section: React.FC<{
  heading: string;
  children: React.ReactNode;
  alt?: boolean;
}> = ({ heading, children, alt }) => (
  <section
    className={`section-padding ${
      alt
        ? "bg-[#F1F5F9] dark:bg-gray-800"
        : "bg-[#F9FAFB] dark:bg-gray-900"
    }`}
  >
    <div className="max-w-3xl mx-auto">
      <h2 className="text-3xl font-bold mb-6 text-domichron-text dark:text-white">
        {heading}
      </h2>
      <div className="space-y-5 text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
        {children}
      </div>
    </div>
  </section>
);

const MojaFinance: React.FC = () => {
  return (
    <main className="min-h-screen bg-[#F9FAFB] dark:bg-gray-900 text-domichron-text dark:text-white font-sans antialiased relative text-sm sm:text-base md:text-lg">
      <Header />

      {/* Hero */}
      <section className="relative px-6 lg:px-20 pt-16 pb-20 bg-[#F9FAFB] dark:bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/"
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-domichron-orange transition"
          >
            ← Back to Domichron
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mt-6 space-y-6"
          >
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-domichron-orange">
              A Domichron venture · In development
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-semibold leading-tight text-domichron-text dark:text-white">
              Moja Finance. A Financial infrastructure for African small businesses.
            </h1>
            <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl">
              So owners can see their money clearly. So banks can lend
              confidently. So businesses that have been overlooked for
              generations finally become bankable.
            </p>
          </motion.div>
        </div>
      </section>

      <Section heading="The problem" alt>
        <p>
          Across Africa, small businesses move money through a fragmented
          landscape of mobile money wallets, bank accounts, payment processors,
          and cash. A typical growing business in Lagos or Nairobi might
          receive payments through five or more different channels in a single
          day, each with its own dashboard, its own reporting format, and its
          own settlement timing.
        </p>
        <p>
          The result is that most owners genuinely don't know if they're
          profitable. They can't reliably tell which customers have paid and
          which haven't. They can't separate gross revenue from the fees and
          reversals quietly eating into it. And they almost never have the kind
          of financial records that a lender needs in order to extend credit.
        </p>
      </Section>

      <Section heading="Why this matters">
        <p>
          Small and medium businesses are the engine of the African economy.
          They make up over 90% of registered businesses across most African
          countries and employ a majority of the workforce. Yet they receive
          only a small fraction of formal commercial credit — in some markets
          less than 10%.
        </p>
        <p>
          The reason is not that banks are unwilling. It's that the businesses
          themselves can't demonstrate what's happening with their money.
          Without records, there's no underwriting. Without underwriting,
          there's no credit. Without credit, businesses stay stuck —
          reinvesting their entire margin to survive instead of expanding,
          hiring, or formalizing.
        </p>
        <p>
          The continent's SME funding gap is estimated in the hundreds of
          billions of dollars. It exists less because of unwillingness on the
          supply side, and more because of an information vacuum on the demand
          side. Reconciliation is the foundation that vacuum sits on top of.
        </p>
      </Section>

      <Section heading="What we're building" alt>
        <p>
          Moja Finance is the financial visibility layer for African small
          businesses.
        </p>
        <p>
          It pulls in every transaction a business receives — across mobile
          money APIs (M-Pesa, MTN MoMo, Airtel Money), bank accounts (via open
          banking integrations), payment processors, and manually entered cash.
          It matches each payment to the corresponding sale, supplier,
          customer, or expense. It surfaces what's actually happening: real-time
          cash position, true profit margins, customer payment behavior,
          supplier costs, fees and leakage.
        </p>
        <p>
          For the owner, this is the first time they can see their business
          clearly. For lenders, the same data — shared with the owner's
          permission — becomes a reliable foundation for credit decisions where
          none existed before.
        </p>
        <p>
          The product is reconciliation on the surface. The deeper bet is that
          clean financial visibility is the missing prerequisite for African
          small businesses to participate fully in the formal economy.
        </p>
      </Section>

      <Section heading="Who it's for">
        <p>
          We're building first for growing small and medium businesses — the
          trader importing goods across borders, the shopkeeper managing
          multiple suppliers, the agro-processor selling into multiple markets,
          the service business juggling cash and digital payments.
        </p>
        <p>
          These are businesses with real transaction volume and real
          complexity, where the cost of not having clarity is high — and where
          the opportunity unlocked by clarity is even higher.
        </p>
      </Section>

      <Section heading="Where we're starting" alt>
        <p>
          Our initial focus is East Africa: Rwanda, Kenya, Uganda, and South
          Sudan. We chose these markets for their dense mobile money
          penetration, their growing fintech infrastructure, and the strength
          of our local networks across the region.
        </p>
        <p>
          The plan is to begin with deep customer discovery — sitting with
          dozens of business owners and lenders on the ground — before serious
          production code is written. Reconciliation infrastructure is the kind
          of work that fails when it's built in isolation from the people it's
          meant to serve.
        </p>
      </Section>

      <Section heading="Timeline">
        <p>
          Moja Finance is currently in design and early development. We
          expect to begin onboarding design-partner businesses across East
          Africa in the second half of 2026, with broader availability
          following as the product matures.
        </p>
      </Section>

      {/* CTA */}
      <section className="section-padding bg-white dark:bg-gray-800 text-center">
        <div className="max-w-2xl mx-auto space-y-6">
          <h2 className="text-3xl font-bold text-domichron-text dark:text-white">
            Get in touch
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300">
            If you're a small business owner, a lender, a fintech operator, or
            a builder interested in this work — we'd love to hear from you.
          </p>
          <button
            type="button"
            onClick={() => navigate("/contact")}
            className="btn-primary inline-block"
          >
            Email us
          </button>
        </div>
      </section>

      <footer className="py-8 border-t border-gray-200 dark:border-gray-800">
        <div className="flex flex-col items-center gap-3 px-6">
          <a
            href={LINKEDIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-base text-gray-700 dark:text-gray-300 hover:text-domichron-orange transition-colors"
          >
            <BsLinkedin className="w-5 h-5" />
            <span>Connect on LinkedIn</span>
          </a>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            © {new Date().getFullYear()} Domichron, Inc. All rights reserved.
          </p>
        </div>
      </footer>

    </main>
  );
};

export default MojaFinance;
